/**
 * Service Map Visualizer using Cytoscape.js (TypeScript)
 * Sprint 24: Enhanced with tier labels, icons, shaded backgrounds, and collapse feature
 */

/**
 * Service tier device structure for visualization
 */
interface ServiceTierDevice {
    device_id: string;
    ip_address: string;
    hostname: string;
    role: string;
    config_files: string[];
}

/**
 * Service tier structure
 */
interface ServiceTier {
    tier_name: string;
    tier_level: number;
    devices: ServiceTierDevice[];
}

/**
 * Service connection structure
 */
interface ServiceConnection {
    from_device: string;
    to_device: string;
    connection_type: string;
    port: number | null;
}

/**
 * Complete service map data structure
 */
interface ServiceMapData {
    service_name: string;
    discovered_at: bigint;
    tiers: ServiceTier[];
    connections: ServiceConnection[];
    total_devices: number;
    discovery_logs: string[];
}

/**
 * Cytoscape node data structure
 */
interface CytoscapeNodeData {
    id: string;
    label: string;
    tier: string;
    tier_level: number;
    ip: string;
    role: string;
    color: string;
    nodeType?: 'device' | 'tier-label' | 'tier-background';
    deviceCount?: number;
}

/**
 * Tier icon and color mapping
 */
interface TierStyle {
    icon: string;
    color: string;
    bgColor: string;
}

/**
 * Service Map Visualizer using Cytoscape.js
 * Provides interactive graph visualization of service topology
 */
export class ServiceMapVisualizer {
    private containerId: string;
    private cy: any;
    private serviceMapData: ServiceMapData | null;
    private collapsedTiers: Set<number>;
    private tierStyles: Record<string, TierStyle>;

    /**
     * Create a new service map visualizer
     */
    constructor(containerId: string) {
        this.containerId = containerId;
        this.cy = null;
        this.serviceMapData = null;
        this.collapsedTiers = new Set();

        // Define tier styles with icons and colors
        this.tierStyles = {
            // Standard Technology Tiers (CSDM)
            'Web Tier': { icon: '🌐', color: '#a78bfa', bgColor: 'rgba(167, 139, 250, 0.15)' },
            'Application Tier': { icon: '⚙️', color: '#34d399', bgColor: 'rgba(52, 211, 153, 0.15)' },
            'Database Tier': { icon: '🗄️', color: '#60a5fa', bgColor: 'rgba(96, 165, 250, 0.15)' },
            'Integration Tier': { icon: '🔗', color: '#fbbf24', bgColor: 'rgba(251, 191, 36, 0.15)' },
            'Infrastructure Tier': { icon: '🖥️', color: '#f472b6', bgColor: 'rgba(244, 114, 182, 0.15)' },
            // Legacy names
            'Load Balancer': { icon: '🌐', color: '#a78bfa', bgColor: 'rgba(167, 139, 250, 0.15)' },
            'Application': { icon: '⚙️', color: '#34d399', bgColor: 'rgba(52, 211, 153, 0.15)' },
            'Database': { icon: '🗄️', color: '#60a5fa', bgColor: 'rgba(96, 165, 250, 0.15)' },
            // Business Service Tiers
            'Channels': { icon: '📱', color: '#a78bfa', bgColor: 'rgba(167, 139, 250, 0.15)' },
            'Business Processing': { icon: '📊', color: '#34d399', bgColor: 'rgba(52, 211, 153, 0.15)' },
            'Business Services': { icon: '💼', color: '#22d3ee', bgColor: 'rgba(34, 211, 238, 0.15)' },
            'Core Services': { icon: '🏛️', color: '#60a5fa', bgColor: 'rgba(96, 165, 250, 0.15)' },
            'Data Layer': { icon: '💾', color: '#818cf8', bgColor: 'rgba(129, 140, 248, 0.15)' },
            'External Integration': { icon: '🔗', color: '#fbbf24', bgColor: 'rgba(251, 191, 36, 0.15)' },
            'Legacy Services': { icon: '🏚️', color: '#f472b6', bgColor: 'rgba(244, 114, 182, 0.15)' },
            // ServiceNow Platform Tiers
            'IT Service Management': { icon: '🎫', color: '#22d3ee', bgColor: 'rgba(34, 211, 238, 0.15)' },
            'IT Operations Management': { icon: '📡', color: '#34d399', bgColor: 'rgba(52, 211, 153, 0.15)' },
            'Enterprise Applications': { icon: '🏢', color: '#a78bfa', bgColor: 'rgba(167, 139, 250, 0.15)' },
            'Platform & Integration': { icon: '🔧', color: '#f472b6', bgColor: 'rgba(244, 114, 182, 0.15)' },
            // Salesforce Platform Tiers
            'Sales & Revenue': { icon: '💰', color: '#22d3ee', bgColor: 'rgba(34, 211, 238, 0.15)' },
            'Customer Service': { icon: '🎧', color: '#34d399', bgColor: 'rgba(52, 211, 153, 0.15)' },
            'Marketing & Commerce': { icon: '📣', color: '#f472b6', bgColor: 'rgba(244, 114, 182, 0.15)' },
            'Data & Analytics': { icon: '📈', color: '#60a5fa', bgColor: 'rgba(96, 165, 250, 0.15)' },
            // BMC Software Platform Tiers
            'IT Service Management (Helix)': { icon: '🎫', color: '#22d3ee', bgColor: 'rgba(34, 211, 238, 0.15)' },
            'IT Operations Management (Helix)': { icon: '📡', color: '#34d399', bgColor: 'rgba(52, 211, 153, 0.15)' },
            'Workload Automation (Control-M)': { icon: '⏱️', color: '#fbbf24', bgColor: 'rgba(251, 191, 36, 0.15)' },
            'Mainframe Management (AMI)': { icon: '🖥️', color: '#a78bfa', bgColor: 'rgba(167, 139, 250, 0.15)' }
        };
    }

    /**
     * Get tier style (icon, color, background color)
     */
    private getTierStyle(tierName: string): TierStyle {
        return this.tierStyles[tierName] || { icon: '📦', color: '#9ca3af', bgColor: 'rgba(156, 163, 175, 0.15)' };
    }

    /**
     * Simplify hostname to a single word label
     * Examples:
     *   "sales.consolidated.bank" → "Sales"
     *   "service.consolidated.bank" → "Service"
     *   "ip-192-168-1-15" → "IP-192-168-1-15"
     */
    private simplifyHostname(hostname: string): string {
        if (!hostname) return 'Unknown';

        // Extract the first part before the first dot
        const firstPart = hostname.split('.')[0];

        // Capitalize first letter
        if (firstPart.length > 0) {
            return firstPart.charAt(0).toUpperCase() + firstPart.slice(1);
        }

        return hostname;
    }

    /**
     * Initialize Cytoscape instance with styles and event listeners
     */
    initialize(): boolean {
        const container = document.getElementById(this.containerId);
        if (!container) {
            console.error(`Container #${this.containerId} not found`);
            return false;
        }

        // @ts-ignore - cytoscape is loaded globally
        this.cy = cytoscape({
            container: container,

            style: [
                // Device node styles
                {
                    selector: 'node[nodeType="device"]',
                    style: {
                        'label': 'data(label)',
                        'text-valign': 'center',
                        'text-halign': 'center',
                        'background-color': 'data(color)',
                        'width': 100,
                        'height': 100,
                        'border-width': 3,
                        'border-color': '#374151',
                        'font-size': 16,
                        'font-weight': 'bold',
                        'color': '#1f2937',
                        'text-wrap': 'wrap',
                        'text-max-width': 95,
                        'overlay-opacity': 0,
                        'shape': 'roundrectangle'
                    }
                },
                // Tier label styles (positioned on right)
                {
                    selector: 'node[nodeType="tier-label"]',
                    style: {
                        'label': 'data(label)',
                        'text-valign': 'center',
                        'text-halign': 'center',
                        'background-color': 'data(color)',
                        'background-opacity': 0.9,
                        'width': 'label',  // Auto-size to fit label
                        'height': 40,
                        'padding': 15,
                        'border-width': 2,
                        'border-color': 'data(color)',
                        'font-size': 11,
                        'font-weight': 'bold',
                        'color': '#ffffff',
                        'text-wrap': 'none',
                        'shape': 'roundrectangle',
                        'cursor': 'pointer'
                    }
                },
                // Tier background styles (shaded area)
                {
                    selector: 'node[nodeType="tier-background"]',
                    style: {
                        'background-color': 'data(bgColor)',
                        'background-opacity': 0.3,
                        'border-width': 2,
                        'border-color': 'data(color)',
                        'border-opacity': 0.5,
                        'shape': 'roundrectangle',
                        'label': '',
                        'events': 'no',
                        'z-index': -1
                    }
                },
                // Selected node
                {
                    selector: 'node:selected',
                    style: {
                        'border-width': 4,
                        'border-color': '#2563eb'
                    }
                },
                // Collapsed tier label
                {
                    selector: 'node[nodeType="tier-label"].collapsed',
                    style: {
                        'background-opacity': 0.6,
                        'border-style': 'dashed'
                    }
                },
                // Edge styles
                {
                    selector: 'edge',
                    style: {
                        'width': 3,
                        'line-color': '#9ca3af',
                        'target-arrow-color': '#9ca3af',
                        'target-arrow-shape': 'triangle',
                        'curve-style': 'bezier',
                        'label': 'data(label)',
                        'font-size': 10,
                        'text-rotation': 'autorotate',
                        'text-margin-y': -10,
                        'color': '#6b7280'
                    }
                },
                // Highlighted edges
                {
                    selector: 'edge.highlighted',
                    style: {
                        'width': 5,
                        'line-color': '#2563eb',
                        'target-arrow-color': '#2563eb'
                    }
                },
                // Hidden elements (for collapse)
                {
                    selector: '.hidden',
                    style: {
                        'display': 'none'
                    }
                }
            ],

            layout: {
                name: 'preset'  // We'll position manually
            },

            userZoomingEnabled: true,
            userPanningEnabled: true,
            boxSelectionEnabled: false,
            autoungrabify: false,
            autounselectify: false
        });

        this.setupEventListeners();
        // Legend removed for cleaner demo presentation

        console.log('✅ Service Map Visualizer initialized (Sprint 24)');
        return true;
    }

    /**
     * Setup click and hover event listeners
     */
    private setupEventListeners(): void {
        if (!this.cy) return;

        // Device node click - highlight dependencies
        this.cy.on('tap', 'node[nodeType="device"]', (event: any) => {
            this.handleNodeClick(event.target);
        });

        // Tier label click - toggle collapse
        this.cy.on('tap', 'node[nodeType="tier-label"]', (event: any) => {
            const tierLevel = event.target.data('tier_level');
            this.toggleTierCollapse(tierLevel);
        });

        // Double click device - show details
        this.cy.on('dbltap', 'node[nodeType="device"]', (event: any) => {
            this.showNodeDetails(event.target);
        });

        // Background click - clear selection
        this.cy.on('tap', (event: any) => {
            if (event.target === this.cy) {
                this.clearSelection();
            }
        });

        // Hover effects
        this.cy.on('mouseover', 'node[nodeType="device"]', (event: any) => {
            event.target.style('cursor', 'pointer');
        });

        this.cy.on('mouseover', 'node[nodeType="tier-label"]', (event: any) => {
            event.target.style('cursor', 'pointer');
        });
    }

    /**
     * Toggle tier collapse/expand
     */
    private toggleTierCollapse(tierLevel: number): void {
        if (!this.cy || !this.serviceMapData) return;

        const tierLabel = this.cy.getElementById(`tier-label-${tierLevel}`);
        const tierBg = this.cy.getElementById(`tier-bg-${tierLevel}`);

        if (this.collapsedTiers.has(tierLevel)) {
            // Expand
            this.collapsedTiers.delete(tierLevel);
            tierLabel.removeClass('collapsed');

            // Show devices and update label
            const tier = this.serviceMapData.tiers.find(t => t.tier_level === tierLevel);
            if (tier) {
                const style = this.getTierStyle(tier.tier_name);
                tierLabel.data('label', `${style.icon} ${tier.tier_name} ▼`);

                // Show devices
                tier.devices.forEach(device => {
                    this.cy.getElementById(device.device_id).removeClass('hidden');
                });

                // Show background
                tierBg.removeClass('hidden');

                // Show edges connected to this tier's devices
                tier.devices.forEach(device => {
                    this.cy.getElementById(device.device_id).connectedEdges().removeClass('hidden');
                });
            }
        } else {
            // Collapse
            this.collapsedTiers.add(tierLevel);
            tierLabel.addClass('collapsed');

            // Hide devices and update label
            const tier = this.serviceMapData.tiers.find(t => t.tier_level === tierLevel);
            if (tier) {
                const style = this.getTierStyle(tier.tier_name);
                tierLabel.data('label', `${style.icon} ${tier.tier_name} (${tier.devices.length}) ▶`);

                // Hide devices
                tier.devices.forEach(device => {
                    this.cy.getElementById(device.device_id).addClass('hidden');
                });

                // Hide background
                tierBg.addClass('hidden');

                // Hide edges connected to this tier's devices
                tier.devices.forEach(device => {
                    this.cy.getElementById(device.device_id).connectedEdges().addClass('hidden');
                });
            }
        }

        // Re-layout
        this.repositionElements();
    }

    /**
     * Render service map from data
     */
    renderServiceMap(serviceMap: ServiceMapData): void {
        if (!this.cy) {
            console.error('Visualizer not initialized');
            return;
        }

        this.serviceMapData = serviceMap;
        this.collapsedTiers.clear();
        console.log('🗺️ Rendering service map:', serviceMap);

        this.cy.elements().remove();

        const elements: any[] = [];
        const existingNodeIds = new Set<string>();

        // Sort tiers by level
        const sortedTiers = [...serviceMap.tiers].sort((a, b) => a.tier_level - b.tier_level);

        // Add device nodes with icons
        sortedTiers.forEach((tier) => {
            const style = this.getTierStyle(tier.tier_name);

            tier.devices.forEach((device) => {
                existingNodeIds.add(device.device_id);
                const displayLabel = this.simplifyHostname(device.hostname || device.ip_address);
                elements.push({
                    group: 'nodes',
                    data: {
                        id: device.device_id,
                        label: `${style.icon}\n${displayLabel}`,
                        tier: tier.tier_name,
                        tier_level: tier.tier_level,
                        ip: device.ip_address,
                        role: device.role,
                        color: style.color,
                        nodeType: 'device'
                    }
                });
            });
        });

        // Add edges from connections
        serviceMap.connections.forEach((conn, index) => {
            if (!existingNodeIds.has(conn.to_device)) {
                existingNodeIds.add(conn.to_device);
                const style = this.getTierStyle('Database Tier');
                const toLabel = this.simplifyHostname(conn.to_device);
                elements.push({
                    group: 'nodes',
                    data: {
                        id: conn.to_device,
                        label: `${style.icon}\n${toLabel}`,
                        tier: 'Database Tier',
                        tier_level: 3,
                        ip: conn.to_device,
                        role: 'inferred-database',
                        color: style.color,
                        nodeType: 'device'
                    }
                });
            }

            if (!existingNodeIds.has(conn.from_device)) {
                existingNodeIds.add(conn.from_device);
                const style = this.getTierStyle('Application Tier');
                const fromLabel = this.simplifyHostname(conn.from_device);
                elements.push({
                    group: 'nodes',
                    data: {
                        id: conn.from_device,
                        label: `${style.icon}\n${fromLabel}`,
                        tier: 'Application Tier',
                        tier_level: 2,
                        ip: conn.from_device,
                        role: 'inferred-application',
                        color: style.color,
                        nodeType: 'device'
                    }
                });
            }

            const label = conn.port !== null ? `${conn.connection_type}:${conn.port}` : conn.connection_type;
            elements.push({
                group: 'edges',
                data: {
                    id: `edge-${index}`,
                    source: conn.from_device,
                    target: conn.to_device,
                    label: label,
                    type: conn.connection_type,
                    port: conn.port
                }
            });
        });

        // Add tier labels (will be positioned later)
        sortedTiers.forEach((tier) => {
            const style = this.getTierStyle(tier.tier_name);

            // Tier label node
            elements.push({
                group: 'nodes',
                data: {
                    id: `tier-label-${tier.tier_level}`,
                    label: `${style.icon} ${tier.tier_name} ▼`,
                    tier: tier.tier_name,
                    tier_level: tier.tier_level,
                    color: style.color,
                    nodeType: 'tier-label',
                    deviceCount: tier.devices.length
                }
            });

            // Tier background node
            elements.push({
                group: 'nodes',
                data: {
                    id: `tier-bg-${tier.tier_level}`,
                    tier: tier.tier_name,
                    tier_level: tier.tier_level,
                    color: style.color,
                    bgColor: style.bgColor,
                    nodeType: 'tier-background'
                }
            });
        });

        // Add elements to graph
        this.cy.add(elements);

        // Position elements
        this.positionElements(sortedTiers);

        console.log(`✅ Rendered ${elements.length} elements`);
    }

    /**
     * Position all elements in a hierarchical layout
     */
    private positionElements(sortedTiers: ServiceTier[]): void {
        const containerWidth = this.cy.width();
        const nodeWidth = 90;
        const nodeHeight = 90;
        const horizontalGap = 40;
        const verticalGap = 80;
        const tierPadding = 30;
        const labelGap = 40;  // Gap between devices and label

        // Calculate max label width based on tier names
        const maxLabelWidth = Math.max(
            ...sortedTiers.map(t => {
                const style = this.getTierStyle(t.tier_name);
                return (style.icon + ' ' + t.tier_name + ' ▼').length * 8 + 30;
            }),
            200  // Minimum width
        );

        let currentY = 50;

        sortedTiers.forEach((tier) => {
            const devices = tier.devices;
            const deviceCount = devices.length;

            if (deviceCount === 0) return;

            // Calculate tier row width
            const rowWidth = deviceCount * nodeWidth + (deviceCount - 1) * horizontalGap;
            const startX = (containerWidth - rowWidth - maxLabelWidth - labelGap) / 2;

            // Position device nodes
            devices.forEach((device, index) => {
                const x = startX + index * (nodeWidth + horizontalGap) + nodeWidth / 2;
                const y = currentY + nodeHeight / 2;

                const node = this.cy.getElementById(device.device_id);
                if (node.length) {
                    node.position({ x, y });
                }
            });

            // Position tier background (shaded area)
            const bgNode = this.cy.getElementById(`tier-bg-${tier.tier_level}`);
            if (bgNode.length) {
                const bgWidth = rowWidth + tierPadding * 2;
                const bgHeight = nodeHeight + tierPadding;
                const bgX = startX + rowWidth / 2;
                const bgY = currentY + nodeHeight / 2;

                bgNode.position({ x: bgX, y: bgY });
                bgNode.style({
                    'width': bgWidth,
                    'height': bgHeight
                });
            }

            // Position tier label on the right (after devices + gap)
            const labelNode = this.cy.getElementById(`tier-label-${tier.tier_level}`);
            if (labelNode.length) {
                const labelX = startX + rowWidth + tierPadding + labelGap + maxLabelWidth / 2;
                const labelY = currentY + nodeHeight / 2;

                labelNode.position({ x: labelX, y: labelY });
            }

            // Move to next tier row
            currentY += nodeHeight + verticalGap;
        });

        // Fit view
        this.cy.fit(undefined, 50);
    }

    /**
     * Reposition elements after collapse/expand
     */
    private repositionElements(): void {
        if (!this.serviceMapData) return;

        const containerWidth = this.cy.width();
        const nodeWidth = 90;
        const nodeHeight = 90;
        const horizontalGap = 40;
        const verticalGap = 80;
        const tierPadding = 30;
        const labelGap = 40;
        const collapsedHeight = 50;

        // Calculate max label width based on tier names
        const maxLabelWidth = Math.max(
            ...this.serviceMapData.tiers.map(t => {
                const style = this.getTierStyle(t.tier_name);
                return (style.icon + ' ' + t.tier_name + ' ▼').length * 8 + 30;
            }),
            200
        );

        let currentY = 50;

        this.serviceMapData.tiers
            .sort((a, b) => a.tier_level - b.tier_level)
            .forEach((tier) => {
                const isCollapsed = this.collapsedTiers.has(tier.tier_level);
                const devices = tier.devices;
                const deviceCount = devices.length;

                if (deviceCount === 0) return;

                const rowWidth = deviceCount * nodeWidth + (deviceCount - 1) * horizontalGap;
                const startX = (containerWidth - rowWidth - maxLabelWidth - labelGap) / 2;

                if (!isCollapsed) {
                    // Position visible device nodes
                    devices.forEach((device, index) => {
                        const x = startX + index * (nodeWidth + horizontalGap) + nodeWidth / 2;
                        const y = currentY + nodeHeight / 2;

                        const node = this.cy.getElementById(device.device_id);
                        if (node.length) {
                            node.animate({
                                position: { x, y },
                                duration: 300
                            });
                        }
                    });

                    // Position tier background
                    const bgNode = this.cy.getElementById(`tier-bg-${tier.tier_level}`);
                    if (bgNode.length) {
                        const bgX = startX + rowWidth / 2;
                        const bgY = currentY + nodeHeight / 2;
                        bgNode.animate({
                            position: { x: bgX, y: bgY },
                            duration: 300
                        });
                    }
                }

                // Position tier label
                const labelNode = this.cy.getElementById(`tier-label-${tier.tier_level}`);
                if (labelNode.length) {
                    const effectiveHeight = isCollapsed ? collapsedHeight : nodeHeight;
                    const labelX = startX + rowWidth + tierPadding + labelGap + maxLabelWidth / 2;
                    const labelY = currentY + effectiveHeight / 2;

                    labelNode.animate({
                        position: { x: labelX, y: labelY },
                        duration: 300
                    });
                }

                // Move to next tier row
                currentY += (isCollapsed ? collapsedHeight : nodeHeight) + verticalGap;
            });

        // Fit view after animation
        setTimeout(() => {
            this.cy.fit(undefined, 50);
        }, 350);
    }

    /**
     * Handle device node click
     */
    private handleNodeClick(node: any): void {
        console.log('🖱️ Node clicked:', node.id());

        this.cy.elements().removeClass('highlighted');
        this.cy.nodes('[nodeType="device"]').style('opacity', 0.3);
        this.cy.edges().style('opacity', 0.2);

        node.style('opacity', 1);

        const connectedEdges = node.connectedEdges();
        const connectedNodes = connectedEdges.connectedNodes();

        connectedNodes.style('opacity', 1);
        connectedEdges.addClass('highlighted').style('opacity', 1);

        this.onNodeSelected(node.data());
        this.showNodeQuickInfo(node);
    }

    /**
     * Clear selection
     */
    clearSelection(): void {
        if (!this.cy) return;

        this.cy.elements().removeClass('highlighted');
        this.cy.nodes('[nodeType="device"]').style('opacity', 1);
        this.cy.edges().style('opacity', 1);

        const quickInfo = document.getElementById('node-quick-info');
        if (quickInfo) quickInfo.classList.add('hidden');

        if (typeof (window as any).onServiceMapNodeCleared === 'function') {
            (window as any).onServiceMapNodeCleared();
        }
    }

    /**
     * Show node details modal
     */
    private showNodeDetails(node: any): void {
        const data = node.data();
        console.log('📊 Node details:', data);

        let deviceInfo: (ServiceTierDevice & { tier: string }) | null = null;
        if (this.serviceMapData) {
            for (const tier of this.serviceMapData.tiers) {
                const device = tier.devices.find(d => d.device_id === data.id);
                if (device) {
                    deviceInfo = { ...device, tier: tier.tier_name };
                    break;
                }
            }
        }

        if (typeof (window as any).showDeviceDetailsModal === 'function' && deviceInfo) {
            (window as any).showDeviceDetailsModal(deviceInfo);
        }
    }

    /**
     * Show quick info tooltip
     */
    private showNodeQuickInfo(node: any): void {
        const data = node.data();
        const quickInfo = document.getElementById('node-quick-info');

        if (!quickInfo) return;

        const style = this.getTierStyle(data.tier);
        const deviceId = data.id;

        quickInfo.innerHTML = `
            <div class="bg-white p-4 rounded-lg shadow-lg border-2" style="border-color: ${style.color}">
                <div class="font-bold text-lg mb-2">${style.icon} ${data.label.replace(style.icon + '\n', '')}</div>
                <div class="text-sm space-y-1">
                    <div><span class="text-gray-600">IP:</span> ${data.ip}</div>
                    <div><span class="text-gray-600">Role:</span> ${data.role}</div>
                    <div><span class="text-gray-600">Tier:</span> ${data.tier}</div>
                </div>
                <button
                    id="view-device-details-btn"
                    class="mt-2 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded border border-blue-300 transition-colors"
                    data-device-id="${deviceId}"
                    data-device-ip="${data.ip}"
                    data-device-role="${data.role}"
                    data-device-tier="${data.tier}"
                >
                    View Details
                </button>
            </div>
        `;

        // Add click handler for the button
        const viewBtn = document.getElementById('view-device-details-btn');
        if (viewBtn) {
            viewBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                const btn = e.target as HTMLElement;
                const deviceInfo = {
                    device_id: btn.dataset.deviceId || deviceId,
                    ip_address: btn.dataset.deviceIp || data.ip,
                    role: btn.dataset.deviceRole || data.role,
                    tier: btn.dataset.deviceTier || data.tier
                };
                console.log('📊 View Details button clicked:', deviceInfo);

                if (typeof (window as any).showDeviceDetailsModal === 'function') {
                    (window as any).showDeviceDetailsModal(deviceInfo);
                } else {
                    console.error('showDeviceDetailsModal function not available');
                }
            });
        }

        quickInfo.classList.remove('hidden');
    }

    /**
     * Node selection callback
     */
    private onNodeSelected(nodeData: CytoscapeNodeData): void {
        console.log('📍 Node selected:', nodeData);

        if (typeof (window as any).onServiceMapNodeSelected === 'function') {
            (window as any).onServiceMapNodeSelected(nodeData);
        }
    }

    /**
     * Filter nodes by search
     */
    filterNodes(query: string): void {
        if (!this.cy) return;

        if (!query) {
            this.cy.nodes('[nodeType="device"]').style('display', 'element');
            return;
        }

        const lowerQuery = query.toLowerCase();
        this.cy.nodes('[nodeType="device"]').forEach((node: any) => {
            const data = node.data();
            const matches =
                data.label.toLowerCase().includes(lowerQuery) ||
                data.ip.toLowerCase().includes(lowerQuery) ||
                data.role.toLowerCase().includes(lowerQuery) ||
                data.tier.toLowerCase().includes(lowerQuery);

            node.style('display', matches ? 'element' : 'none');
        });
    }

    /**
     * Clear graph
     */
    clearGraph(): void {
        if (!this.cy) return;
        this.cy.elements().remove();
    }

    /**
     * Get node count
     */
    getNodeCount(): number {
        if (!this.cy) return 0;
        return this.cy.nodes('[nodeType="device"]').length;
    }

    /**
     * Reset view
     */
    resetView(): void {
        if (this.cy) {
            this.cy.fit(undefined, 50);
            this.cy.center();
            this.clearSelection();
        }
    }

    /**
     * Export as PNG
     */
    exportImage(): void {
        if (this.cy) {
            const png64 = this.cy.png({ full: true, scale: 2 });
            const link = document.createElement('a');
            link.href = png64;
            link.download = `service-map-${this.serviceMapData?.service_name || 'export'}.png`;
            link.click();
        }
    }

    /**
     * Destroy visualizer
     */
    destroy(): void {
        if (this.cy) {
            this.cy.destroy();
            this.cy = null;
        }

        // Remove legend
        const legend = document.getElementById('service-map-legend');
        if (legend) legend.remove();
    }
}

// Export for global use
declare global {
    interface Window {
        ServiceMapVisualizer: typeof ServiceMapVisualizer;
    }
}

window.ServiceMapVisualizer = ServiceMapVisualizer;
